import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Input } from '@repo/ui/src/components/ui/input';
import { cn } from '@repo/ui/src/lib/utils';
import { can, BRAND_HEX_RE, BRAND_PRESETS, DEFAULT_BRAND_COLOR, brandOnColor } from '@repo/shared-types';
import { Lock, Shield } from 'lucide-react';
import { formatContactPhone, normalizeContactPhone } from '@repo/validation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgSettings, useUpdateOrg } from '../../../hooks/useOrgSettings';
import { useSubscription } from '../../../hooks/useSubscription';
import { useAuthStore } from '../../../stores/authStore';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { LogoCropDialog } from './LogoCropDialog';
import { SettingRow } from './SettingRow';

/** The band a parent actually sees (sổ liên lạc, invoice): the chosen colour,
 *  the on-colour text `brandOnColor` picks by contrast, a tiny kicker and the
 *  org name in `font-display`. Reused by every gating state below so the
 *  locked/pending/editable rows never drift from what ships. */
function BrandColorPreview({ color, orgName }: { color: string; orgName: string }) {
  return (
    <div
      className="flex h-16 flex-col justify-center gap-0.5 rounded-md px-3"
      style={{ backgroundColor: color, color: brandOnColor(color) }}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider opacity-90">
        Sổ liên lạc điện tử
      </span>
      <span className="truncate font-display text-sm font-semibold">{orgName}</span>
    </div>
  );
}

/** Plain, non-interactive rendering: the pending window before the plan is
 *  known, and what a MEMBER sees regardless of plan (§4.4 — pending is never
 *  rendered as locked; `canEdit` gates the picker entirely, not just saving). */
function BrandColorReadOnly({ color, orgName }: { color: string | null; orgName: string }) {
  const { t } = useTranslation('settings');
  return (
    <SettingRow label={t('profile.org.brandColor')}>
      <div className="space-y-2">
        <BrandColorPreview color={color ?? DEFAULT_BRAND_COLOR} orgName={orgName} />
        <p className="text-xs text-surface-400">{t('profile.org.brandColorHint')}</p>
      </div>
    </SettingRow>
  );
}

/** Below `allowsProgressLink` (Pro+): the eight presets are shown but inert,
 *  and clicking anywhere on the row opens the same upgrade dialog the rest of
 *  the app uses for this flag (see ReviewTargetsCard.tsx). */
function BrandColorLocked() {
  const { t } = useTranslation('settings');
  return (
    <SettingRow label={t('profile.org.brandColor')}>
      <button
        type="button"
        onClick={() => useUpgradeDialogStore.getState().open('progressLink')}
        className="block w-full space-y-2 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          {BRAND_PRESETS.map((preset) => (
            <span
              key={preset.hex}
              aria-hidden
              className="h-7 w-7 shrink-0 rounded-full border border-surface-200 opacity-50"
              style={{ backgroundColor: preset.hex }}
            />
          ))}
        </div>
        <p className="flex items-center gap-1.5 text-xs text-surface-400">
          <Lock className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          {t('profile.org.brandColorLocked')}
        </p>
      </button>
    </SettingRow>
  );
}

/** The editable picker (OWNER/ADMIN, plan allows). Keyed by `savedColor` from
 *  the caller so a fresh save (or another tab's change landing via refetch)
 *  remounts this with a freshly-seeded draft instead of drifting from it —
 *  same reasoning as the org-name field seeding its draft only at edit start. */
function BrandColorEditor({
  savedColor,
  orgName,
  updateOrg,
}: {
  savedColor: string | null;
  orgName: string;
  updateOrg: ReturnType<typeof useUpdateOrg>;
}) {
  const { t } = useTranslation('settings');
  const isSavedCustom = savedColor !== null && !BRAND_PRESETS.some((p) => p.hex === savedColor);
  const [draft, setDraft] = useState<string | null>(savedColor);
  const [customHex, setCustomHex] = useState(isSavedCustom ? savedColor! : '');

  const isDirty = draft !== savedColor;
  const previewColor = draft ?? DEFAULT_BRAND_COLOR;

  function selectPreset(hex: string) {
    setDraft(hex);
    setCustomHex('');
  }

  function handleCustomHexChange(value: string) {
    setCustomHex(value);
    const normalized = value.trim().toLowerCase();
    if (BRAND_HEX_RE.test(normalized)) setDraft(normalized);
  }

  function handleResetToDefault() {
    setDraft(null);
    setCustomHex('');
    updateOrg.mutate({ brandColor: null });
  }

  return (
    <SettingRow label={t('profile.org.brandColor')}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {BRAND_PRESETS.map((preset) => {
            const selected = draft === preset.hex || (draft === null && preset.hex === DEFAULT_BRAND_COLOR);
            return (
              <button
                key={preset.hex}
                type="button"
                aria-label={preset.name}
                onClick={() => selectPreset(preset.hex)}
                className={cn(
                  'h-7 w-7 shrink-0 rounded-full border transition-shadow',
                  selected
                    ? 'border-transparent ring-2 ring-brand-500 ring-offset-2'
                    : 'border-surface-200 hover:ring-1 hover:ring-surface-300 hover:ring-offset-1',
                )}
                style={{ backgroundColor: preset.hex }}
              />
            );
          })}
          <Input
            value={customHex}
            onChange={(e) => handleCustomHexChange(e.target.value)}
            placeholder="#6b2140"
            aria-label={t('profile.org.brandColorCustom')}
            className="h-7 w-28 text-xs"
          />
        </div>

        <BrandColorPreview color={previewColor} orgName={orgName} />

        <p className="text-xs text-surface-400">{t('profile.org.brandColorHint')}</p>

        <div className="flex items-center gap-2">
          <Button size="sm" disabled={!isDirty || updateOrg.isPending} onClick={() => updateOrg.mutate({ brandColor: draft })}>
            {t('profile.org.brandColorSave')}
          </Button>
          {draft !== null && (
            <Button variant="outline" size="sm" disabled={updateOrg.isPending} onClick={handleResetToDefault}>
              {t('profile.org.brandColorReset')}
            </Button>
          )}
        </div>
      </div>
    </SettingRow>
  );
}

/** The three-state gate (INVARIANTS §4.4): pending is read-only, never
 *  locked; `allowsProgressLink === false` locks with an upgrade CTA; `true`
 *  is the full picker. `canEdit` (manage:settings) is checked first — a
 *  MEMBER never sees the locked/upgrade affordance, only the read-only band. */
function BrandColorRow({
  orgName,
  canEdit,
  updateOrg,
}: {
  orgName: string;
  canEdit: boolean;
  updateOrg: ReturnType<typeof useUpdateOrg>;
}) {
  const { data: orgSettings, isPending: orgSettingsPending } = useOrgSettings();
  const { data: subscription, isPending: subscriptionPending } = useSubscription();
  const savedColor = orgSettings?.brandColor ?? null;

  if (subscriptionPending || !canEdit) {
    return <BrandColorReadOnly color={savedColor} orgName={orgName} />;
  }

  const allowsProgressLink = subscription?.plan.allowsProgressLink === true;
  if (!allowsProgressLink) {
    return <BrandColorLocked />;
  }

  if (orgSettingsPending) {
    return <BrandColorReadOnly color={savedColor} orgName={orgName} />;
  }

  return (
    <BrandColorEditor key={savedColor ?? 'default'} savedColor={savedColor} orgName={orgName} updateOrg={updateOrg} />
  );
}

export function OrganizationCard() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const organization = useAuthStore((s) => s.organization);
  const role = useAuthStore((s) => s.role);
  const { data: orgSettings } = useOrgSettings();
  const updateOrg = useUpdateOrg();

  const canEdit = role ? can(role, 'manage:settings') : false;
  const currentName = organization?.name ?? '';
  const logoUrl = orgSettings?.logoUrl ?? organization?.logoUrl ?? null;

  // Name edit mode. The draft is seeded from the store only at the moment
  // "Sửa" is clicked — no effect-based sync is needed since the input isn't
  // mounted until then.
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(currentName);
  const [cropOpen, setCropOpen] = useState(false);

  const startEditingName = () => {
    setName(currentName);
    setEditingName(true);
  };

  const trimmedName = name.trim();
  const canSaveName = trimmedName.length > 0 && trimmedName !== currentName && !updateOrg.isPending;

  // Contact phone: same edit-mode shape as the name. `orgSettings.contactPhone`
  // is the owner's half of the payload (null for a MEMBER, who cannot edit
  // here anyway) and is already in the stored `0xxxxxxxxx` form.
  const currentPhone = orgSettings?.contactPhone ?? null;
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const startEditingPhone = () => {
    setPhone(currentPhone ?? '');
    setPhoneInvalid(false);
    setEditingPhone(true);
  };
  const savePhone = () => {
    const normalized = normalizeContactPhone(phone);
    if (!normalized) {
      setPhoneInvalid(true);
      return;
    }
    updateOrg.mutate({ contactPhone: normalized }, { onSuccess: () => setEditingPhone(false) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('profile.org.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Role — always read-only */}
        <SettingRow label={t('profile.role')}>
          <div className="flex items-center gap-2">
            <Shield className="h-3.5 w-3.5 text-surface-400" strokeWidth={1.8} />
            <Badge
              variant="outline"
              className={
                role === 'OWNER'
                  ? 'text-brand-700 border-brand-200 bg-brand-50'
                  : role === 'ADMIN'
                    ? 'text-blue-700 border-blue-200 bg-blue-50'
                    : 'text-surface-600 border-surface-200 bg-surface-50'
              }
            >
              {tc(`roles.${role || ''}`) || role}
            </Badge>
          </div>
        </SettingRow>

        {/* Org name — editable only for OWNER/ADMIN */}
        <SettingRow
          label={t('profile.org.name')}
          action={
            canEdit &&
            !editingName && (
              <Button variant="outline" size="sm" onClick={startEditingName}>
                {t('profile.edit')}
              </Button>
            )
          }
        >
          {editingName ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="max-w-xs"
                autoFocus
              />
              <Button
                size="sm"
                disabled={!canSaveName}
                onClick={() =>
                  updateOrg.mutate({ name: trimmedName }, { onSuccess: () => setEditingName(false) })
                }
              >
                {updateOrg.isPending ? t('profile.saving') : t('profile.save')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditingName(false)}>
                {t('profile.cancel')}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-surface-700">{currentName || '—'}</p>
          )}
        </SettingRow>

        {/* Contact Zalo number — the one the ContactPhoneBanner asks for;
         *  editable only for OWNER/ADMIN. A MEMBER sees nothing here: the
         *  payload nulls it for them (INVARIANTS §4.11). */}
        {canEdit && (
          <SettingRow
            label={t('profile.org.contactPhone')}
            action={
              !editingPhone && (
                <Button variant="outline" size="sm" onClick={startEditingPhone}>
                  {t('profile.edit')}
                </Button>
              )
            }
          >
            {editingPhone ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (phoneInvalid) setPhoneInvalid(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') savePhone();
                    }}
                    placeholder="0912 345 678"
                    aria-invalid={phoneInvalid || undefined}
                    className={cn('max-w-[200px]', phoneInvalid && 'border-red-400')}
                    autoFocus
                  />
                  <Button size="sm" disabled={updateOrg.isPending || phone.trim() === ''} onClick={savePhone}>
                    {updateOrg.isPending ? t('profile.saving') : t('profile.save')}
                  </Button>
                  {currentPhone && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={updateOrg.isPending}
                      onClick={() => updateOrg.mutate({ contactPhone: null }, { onSuccess: () => setEditingPhone(false) })}
                    >
                      {t('profile.org.contactPhoneClear')}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setEditingPhone(false)}>
                    {t('profile.cancel')}
                  </Button>
                </div>
                {phoneInvalid && <p className="text-xs text-red-600">{t('profile.org.contactPhoneInvalid')}</p>}
              </div>
            ) : (
              <div>
                <p className="text-sm text-surface-700">{currentPhone ? formatContactPhone(currentPhone) : t('profile.org.contactPhoneNone')}</p>
                <p className="mt-1 text-xs text-surface-400">{t('profile.org.contactPhoneHint')}</p>
              </div>
            )}
          </SettingRow>
        )}

        {/* Logo — editable only for OWNER/ADMIN */}
        <SettingRow
          label={t('profile.org.logo')}
          action={
            canEdit && (
              <Button variant="outline" size="sm" onClick={() => setCropOpen(true)}>
                {t('profile.org.changeLogo')}
              </Button>
            )
          }
        >
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={currentName}
                className="w-16 h-16 rounded-sm border border-surface-200 object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-sm border border-dashed border-surface-200 flex items-center justify-center text-[11px] text-surface-400 text-center px-1">
                {t('profile.org.noLogo')}
              </div>
            )}
            <p className="text-xs text-surface-400 max-w-xs">{t('profile.org.logoHint')}</p>
          </div>
        </SettingRow>

        {/* Brand colour (THI-263) — gated by allowsProgressLink (Pro+), see
         *  BrandColorRow above for the three-state read. */}
        <BrandColorRow orgName={currentName} canEdit={canEdit} updateOrg={updateOrg} />

        {!canEdit && <p className="mt-4 text-xs text-surface-400">{t('profile.org.readOnly')}</p>}
      </CardContent>

      {canEdit && (
        <LogoCropDialog
          open={cropOpen}
          onOpenChange={setCropOpen}
          onUploaded={(fileUrl) => updateOrg.mutate({ logoUrl: fileUrl })}
        />
      )}
    </Card>
  );
}
